import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Smartphone, QrCode, Trash2, Shield } from 'lucide-react';
import QRCode from 'qrcode';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScannerAuthorization({ eventId }) {
  const [authorizations, setAuthorizations] = useState([]);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [authResponse, setAuthResponse] = useState(null);
  const [authConfig, setAuthConfig] = useState({
    scope: 'event',
    duration: null
  });

  useEffect(() => {
    fetchAuthorizations();
  }, [eventId]);

  const fetchAuthorizations = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}/authorized-devices`);
      setAuthorizations(response.data);
    } catch (error) {
      console.error('Error fetching authorizations:', error);
    }
  };

  const generateAuthQR = async () => {
    try {
      const response = await axios.post(`${API_URL}/events/${eventId}/generate-auth-qr`, authConfig);
      
      // Save response with PIN code
      setAuthResponse(response.data);
      
      // Generate QR code with URL
      const qrCode = await QRCode.toDataURL(response.data.qr_data, {
        width: 300,
        margin: 2
      });
      
      setQrDataUrl(qrCode);
      
      toast.success('QR di autorizzazione e PIN generati!');
      
      // Refresh authorizations list
      fetchAuthorizations();
    } catch (error) {
      toast.error('Errore durante la generazione QR');
    }
  };

  const revokeAuthorization = async (authId) => {
    if (!window.confirm('Sei sicuro di voler revocare questa autorizzazione?')) return;
    
    try {
      await axios.delete(`${API_URL}/scanner-auth/${authId}`);
      toast.success('Autorizzazione revocata');
      fetchAuthorizations();
    } catch (error) {
      toast.error('Errore durante la revoca');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestione Scanner</h2>
          <p className="text-gray-600 mt-1">Autorizza dispositivi per la scansione biglietti</p>
        </div>
        <Button onClick={() => setShowAuthDialog(true)}>
          <Smartphone className="w-4 h-4 mr-2" />
          Autorizza Nuovo Device
        </Button>
      </div>

      {/* Authorization Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Autorizza Nuovo Scanner</DialogTitle>
          </DialogHeader>
          
          {!qrDataUrl ? (
            <div className="space-y-4">
              <div>
                <Label>Tipo Autorizzazione</Label>
                <Select 
                  value={authConfig.scope} 
                  onValueChange={(value) => setAuthConfig({...authConfig, scope: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="event">Solo questo evento</SelectItem>
                    <SelectItem value="permanent">Permanente (tutti gli eventi)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Durata</Label>
                <Select 
                  value={authConfig.duration?.toString() || 'unlimited'} 
                  onValueChange={(value) => setAuthConfig({
                    ...authConfig, 
                    duration: value === 'unlimited' ? null : parseInt(value)
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unlimited">Illimitata</SelectItem>
                    <SelectItem value="24">24 ore</SelectItem>
                    <SelectItem value="48">48 ore</SelectItem>
                    <SelectItem value="168">1 settimana</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={generateAuthQR} className="w-full">
                <QrCode className="w-4 h-4 mr-2" />
                Genera QR Autorizzazione
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* PIN Code Display */}
              {authResponse?.pin_code && (
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-lg text-center mb-4">
                  <p className="text-sm font-medium mb-2 opacity-90">Codice PIN da Inserire:</p>
                  <div className="text-6xl font-bold tracking-widest font-mono">
                    {authResponse.pin_code}
                  </div>
                  <p className="text-xs mt-3 opacity-75">Inserisci questo codice sul dispositivo mobile</p>
                </div>
              )}
              
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <img src={qrDataUrl} alt="QR Autorizzazione" className="w-full" />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900 font-semibold mb-2">
                  📱 Istruzioni Rapide:
                </p>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Apri la <strong>fotocamera</strong> del telefono</li>
                  <li>Inquadra il <strong>QR code</strong> qui sopra</li>
                  <li>Tap sul link che appare</li>
                  <li>Inserisci il <strong>codice PIN</strong> a 6 cifre</li>
                  <li>✅ Device autorizzato!</li>
                </ol>
              </div>
              
              <Button 
                onClick={() => {
                  setQrDataUrl('');
                  setAuthResponse(null);
                  setShowAuthDialog(false);
                }} 
                variant="outline" 
                className="w-full"
              >
                Chiudi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Authorized Devices List */}
      {authorizations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun device autorizzato</h3>
            <p className="text-gray-600 mb-6">Autorizza dispositivi per permettere la scansione biglietti</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Dispositivi Autorizzati ({authorizations.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scope</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scadenza</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creato</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {authorizations.map(auth => (
                    <tr key={auth.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {auth.device_info?.user_agent ? 
                                auth.device_info.user_agent.substring(0, 50) + '...' : 
                                'Device non attivato'
                              }
                            </p>
                            {auth.device_info?.ip && (
                              <p className="text-xs text-gray-500">IP: {auth.device_info.ip}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          auth.scope === 'permanent' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {auth.scope === 'permanent' ? 'Permanente' : 'Solo questo evento'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {auth.expires_at ? 
                          new Date(auth.expires_at).toLocaleString('it-IT') : 
                          'Mai'
                        }
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(auth.created_at).toLocaleString('it-IT')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => revokeAuthorization(auth.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
